import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatPrice, formatQty } from "@/lib/utils";
import { fmtPriceForSymbol } from './DcaUtils';
import { useLatestValid } from '../../hooks/useLatestValid';
import { router } from '@inertiajs/react';

import { Play, Pause, Settings, Zap, Square, Activity } from "lucide-react";

interface PositionCardProps {
  bot: any;
  cycle: any;
  leverage: number;
  curSym: string;
  onManage: (side: string) => void;
  isPaused: boolean;
  disabled?: boolean;
  isPrivate?: boolean;
  statusMessage?: string;
}

export const PositionCard: React.FC<PositionCardProps> = ({ bot, cycle, leverage, curSym, onManage, isPaused, disabled, isPrivate, statusMessage }) => {
  if (!cycle) return null;
  const isLong = cycle.side === 'long';
  const isStopAtTp = bot.stop_at_tp_sides?.includes(cycle.side);
  const tpThresholdStep = Number(bot.tp_threshold_step || (bot.settings || {})['tp_threshold_step'] || 0);
  const tpThresholdPct = Number(bot.tp_threshold_pct || (bot.settings || {})['tp_threshold_pct'] || 0);
  const useDynamicTp = tpThresholdStep > 0 && tpThresholdPct > 0;
  const side = cycle.side.toLowerCase();
  const tpOverride = bot.settings?.[`${side}_tp_pct_override`];
  const slPct = bot.settings?.[`${side}_stop_loss_pct`];
  
  const isExitAtBreakeven = bot.exit_at_breakeven_sides?.includes(cycle.side);

  const handleToggleExitAtBreakeven = () => {
    router.post(`/dca/${bot.id}/toggle_exit_at_breakeven`, { side: cycle.side }, { preserveScroll: true });
  };

  const [roe, isRoeStale] = useLatestValid(cycle.roe !== null && cycle.roe !== undefined ? Number(cycle.roe) : null);
  const [unrealizedPnl, isPnlStale] = useLatestValid(cycle.unrealized_pnl !== null && cycle.unrealized_pnl !== undefined ? Number(cycle.unrealized_pnl) : null);

  const filledCnt = cycle.filled_steps;
  const totalSteps = cycle.total_steps;
  const initialMargin = Number(cycle.total_cost || 0);
  const avgPrice = Number(cycle.avg_price || 0);
  const totalQty = Number(cycle.total_qty || 0);

  const getStatusIcon = () => {
    if (isStopAtTp) return <Square className="w-3.5 h-3.5 text-danger fill-current" />;
    if (isPaused) return <Pause className="w-3.5 h-3.5 text-amber-500 fill-current" />;
    return <Zap className="w-3.5 h-3.5 text-success fill-current" />;
  };

  return (
    <div className={`rounded-2xl p-5 border-2 transition-all ${isLong ? 'bg-success/5 border-success/20' : 'bg-danger/5 border-danger/20'}`}>
      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className={`w-2 h-2 rounded-full ${isLong ? 'bg-success animate-pulse' : 'bg-danger animate-pulse'}`} />
          <p className={`text-[11px] font-black uppercase tracking-wider ${isLong ? 'text-success' : 'text-danger'}`}>
            {isLong ? 'Long Position' : 'Short Position'}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onManage(cycle.side)}
              disabled={disabled}
              className="h-7 px-3 text-[11px] font-black bg-background hover:bg-muted/10 border-border rounded-lg transition-all flex items-center gap-1.5"
            >
              {getStatusIcon()}
              포지션 관리
            </Button>
            <Button
              variant={isExitAtBreakeven ? "default" : "outline"}
              size="sm"
              onClick={handleToggleExitAtBreakeven}
              disabled={disabled}
              className={`h-7 px-3 text-[11px] font-black rounded-lg transition-all flex items-center gap-1.5 ${
                isExitAtBreakeven
                  ? "bg-amber-500 hover:bg-amber-600 text-white shadow-sm shadow-amber-500/20"
                  : "bg-background hover:bg-amber-500/10 border-border text-muted-foreground hover:text-amber-600"
              }`}
            >
              {/* <Activity className={`w-3.5 h-3.5 ${isExitAtBreakeven ? "animate-pulse" : ""}`} /> */}
              {isExitAtBreakeven ? "본절청산 대기中" : "본절청산예약"}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${isLong ? 'bg-success hover:bg-success/80' : 'bg-danger hover:bg-danger/80'} text-white border-none text-[10px] px-2 py-0.5 font-bold shadow-sm`}>
            Scaling {filledCnt} / {totalSteps}
          </Badge>
        </div>
      </div>

      {/* 실시간 상태 배지 영역 */}
      {(statusMessage?.includes('물타기') || statusMessage?.includes('슈퍼 본전')) && (
        <div className="flex flex-wrap gap-1.5 mb-4 -mt-2">
          {statusMessage?.includes('물타기') && (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[9px] font-black px-2 py-0.5 animate-pulse">
              {statusMessage.includes('억제') ? '추매 억제 중' : '추매 제한됨'}
            </Badge>
          )}
          {statusMessage?.includes('슈퍼 본전') && (
            <Badge variant="outline" className="bg-danger/10 text-danger border-danger/20 text-[9px] font-black px-2 py-0.5 animate-pulse">
              슈퍼 본전 탈출 가동
            </Badge>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">보유 수량</span>
            <span className="text-sm font-black text-foreground">{isPrivate ? '****' : formatQty(totalQty, 6)} <span className="text-[10px] opacity-40 uppercase">Qty</span></span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">평균 단가</span>
            <span className="text-sm font-black text-foreground">{curSym}{fmtPriceForSymbol(avgPrice, bot.symbol)}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">투입 증거금 (Margin)</span>
            <span className="text-sm font-black text-foreground">{isPrivate ? '****' : `${curSym}${formatPrice(initialMargin, 2)}`}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] text-primary font-bold uppercase tracking-tighter">포지션 규모 (Notional)</span>
            <span className="text-sm font-black text-primary">{isPrivate ? '****' : `${curSym}${formatPrice(initialMargin * leverage, 2)}`}</span>
          </div>
          <div className="flex flex-col gap-0.5 pt-2 border-t border-border/10 mt-2">
            <span className="text-[10px] text-success font-bold uppercase tracking-tighter">목표 익절</span>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-black text-success">{isPrivate ? '****' : `+${cycle.tp_pct || bot.tp_pct || 0}%`}</span>
            </div>
            {cycle.tp_price && Number(cycle.tp_price) > 0 && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-tighter">예정 익절가</span>
                <span className="text-xs font-black text-success">{isPrivate ? '****' : `${curSym}${fmtPriceForSymbol(cycle.tp_price, bot.symbol)}`}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-1 mt-1">
              {useDynamicTp && (
                <Badge variant="outline" className="w-fit bg-success/10 text-success border-success/20 text-[9px] font-black px-2 py-0.5">
                  동적 익절: {tpThresholdStep}단계부터 ROE {tpThresholdPct}%
                </Badge>
              )}
              {tpOverride !== undefined && tpOverride !== null && tpOverride !== '' && (
                <Badge variant="outline" className="w-fit bg-indigo-500/10 text-indigo-400 border-indigo-500/20 text-[9px] font-black px-2 py-0.5">
                  개별 익절: {tpOverride}%
                </Badge>
              )}
            </div>
          </div>
          {slPct === -1 ? (
            <div className="flex flex-col gap-0.5 pt-2 border-t border-border/10 mt-1">
              <span className="text-[10px] text-danger font-bold uppercase tracking-tighter">손절 기준가</span>
              <span className="text-xs font-black text-danger/60 flex items-center gap-1">
                <Badge variant="outline" className="bg-danger/10 text-danger border-none text-[8px] font-black h-4 px-1.5 py-0">
                  손절 OFF
                </Badge>
              </span>
            </div>
          ) : (
            cycle.sl_price && Number(cycle.sl_price) > 0 && (
              <div className="flex flex-col gap-0.5 pt-2 border-t border-border/10 mt-1">
                <span className="text-[10px] text-danger font-bold uppercase tracking-tighter">손절 기준가</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-black text-danger/80">{isPrivate ? '****' : `${curSym}${fmtPriceForSymbol(cycle.sl_price, bot.symbol)}`}</span>
                  {slPct !== undefined && slPct !== null && slPct !== '' && slPct !== 0 && slPct !== -1 && (
                    <Badge variant="outline" className="bg-danger/10 text-danger border-danger/20 text-[8px] font-black px-1.5 py-0 h-4">
                      {slPct}%
                    </Badge>
                  )}
                </div>
              </div>
            )
          )}
        </div>

        <div className="flex flex-col items-end justify-center gap-1.5">
          <div className="text-right">
            <p className={`text-2xl font-black tabular-nums leading-none ${isRoeStale ? 'opacity-40' : ''} ${roe === null ? 'text-muted-foreground/30' : (Number(roe) >= 0 ? 'text-success' : 'text-danger')}`}>
              {isPrivate ? '****' : (roe === null ? '—' : `${Number(roe) >= 0 ? '+' : ''}${Number(roe).toFixed(2)}%`)}
            </p>
            <p className="text-[10px] text-muted-foreground font-bold mt-1 uppercase tracking-tight">ROE (Leveraged)</p>
          </div>

          <div className={`px-3 py-1.5 rounded-xl border flex flex-col items-end ${isPnlStale ? 'opacity-40' : ''} ${unrealizedPnl === null ? 'bg-muted/5 border-border/10' : (Number(unrealizedPnl) >= 0 ? 'bg-success/10 border-success/20' : 'bg-danger/10 border-danger/20')}`}>
            <span className="text-[9px] text-muted-foreground font-bold uppercase mb-0.5">평가 손익</span>
            <span className={`text-sm font-black tabular-nums ${unrealizedPnl === null ? 'text-muted-foreground/30' : (Number(unrealizedPnl) >= 0 ? 'text-success' : 'text-danger')}`}>
              {isPrivate ? '****' : (unrealizedPnl === null ? '—' : `${Number(unrealizedPnl) < 0 ? '-' : (Number(unrealizedPnl) > 0 ? '+' : '')}${curSym}${formatPrice(Math.abs(Number(unrealizedPnl)), 2)}`)}
            </span>
          </div>
        </div>
      </div>

      {statusMessage && (
        <div className="mt-4 pt-3 border-t border-border/10">
          <p className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-tight flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> {statusMessage}
          </p>
        </div>
      )}
    </div>
  );
};
